import styled from "styled-components";
import { modalProps } from "../../../../Component/Interface/Modal";
import { RowView, RowView2 } from "../../../../Component/style/common_style";
import { amountFormat } from "../../../../Component/tool/Tools";
import CompleteModal from "./CompleteModal";
import { useEffect, useRef, useState } from "react";
import api from "../../../../Component/axios_Interceptor/api";
import url from "../../../../Component/server";
import { useDepositName } from './useDepositName';

interface CheckModalProps extends modalProps {
  chargeCoin: number;
  myCoin: number | null;
  handleCharge: () => void;
  handleReload: () => void;
}

const CheckModal = ({
  visible,
  handleModal,
  chargeCoin,
  myCoin,
  handleCharge,
  handleReload,
}: CheckModalProps) => {
  const closeModal = () => {
    if (!inFlight.current) handleModal();
  };
  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const [completeVisible, setCompleteVisible] = useState(false);
  const handleCompleteModal = () => {
    setCompleteVisible(value => !value);
  };
  const member = useDepositName(visible);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [uncertain, setUncertain] = useState(false);
  const inFlight = useRef(false);
  const submitted = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // 코인 충전
  const coin_charging = async () => {
    if (inFlight.current || submitted.current || uncertain || member.loading || member.error) return;
    if (!Number.isSafeInteger(chargeCoin) || chargeCoin < 1 || chargeCoin > 2147483647) {
      setError('신청할 코인을 1 이상의 정수로 입력해주세요.');
      return;
    }
    if (!member.name.trim()) { setError('입금자명을 입력해주세요.'); return; }
    inFlight.current = true;
    setSubmitting(true);
    setError('');
    try {
      const res = await api.post(url.Communication_url.coin_charge, {
        log_type: "충전",
        log_con: "코인 충전 입금 대기",
        coin: chargeCoin,
        requested_depositor: member.name.trim(),
      });
      if (res.data !== '성공') throw new Error('충전 신청 결과를 확인하지 못했습니다.');
      submitted.current = true;
      if (mounted.current) { setCompleteVisible(true); handleReload(); }
    } catch (failure: any) {
      if (!mounted.current) return;
      const status = failure?.response?.status;
      if (!status || status >= 500) {
        setUncertain(true);
        setError('신청 결과를 확인하지 못했습니다. 코인 내역의 입금 대기 신청을 확인한 후 다시 진행해주세요.');
        handleReload();
      } else {
        const message = failure?.response?.data?.message;
        setError(typeof message === 'string' ? message : '충전 신청을 접수하지 못했습니다. 다시 확인해주세요.');
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) setSubmitting(false);
    }
  };

  return (
    <Container
      style={visible ? {} : { display: "none" }}
      onClick={stopPropagation}
    >
      <ModalBox>
        <Title>코인 충전 신청</Title>
        <ContentDiv>
          <div className="medium">무통장 입금</div>
          <div>아래 계좌에 입금 후 운영자가 입금 내역을 확인하면 코인이 지급됩니다.</div>
        </ContentDiv>
        <SubTitle>입금 정보</SubTitle>
        <RowView2 style={{ marginBottom: 30 }}>
          <InfoDiv>
            <div>입금은행/예금주</div>
            <div>입금계좌</div>
            <div>입금대기금액</div>

          </InfoDiv>
          <InfoDiv>
            <div className="bankInfo">광주은행 / 이헌영(링크캠퍼스)</div>
            <div className="bankInfo">1121-024-896929</div>
            <div className="bankInfo">
              {amountFormat(chargeCoin * 100)}원
              <div className="gray">({amountFormat(chargeCoin)}C)</div>
            </div>

          </InfoDiv>
        </RowView2>
        <DepositorField>
          <label htmlFor="coin-depositor">입금자명</label>
          <input id="coin-depositor" value={member.name} onChange={event => member.setName(event.target.value)}
            maxLength={100} placeholder={member.loading ? '조회 중…' : '실제 입금자명을 입력해주세요'}
            required disabled={member.loading || member.error || submitting || uncertain || submitted.current} />
        </DepositorField>
        {member.error && <p role="alert">등록된 입금자명을 불러오지 못했습니다. <button type="button" onClick={member.retry}>다시 조회</button></p>}
        {error && <p role="alert">{error}</p>}
        <BtnDiv>
          <CancelBtn type="button" disabled={submitting} onClick={closeModal}>닫기</CancelBtn>
          <ApplyBtn type="button" disabled={submitting || uncertain || member.loading || member.error || submitted.current || !member.name.trim()} onClick={coin_charging}>{submitting ? '신청 중…' : '충전 신청 접수'}</ApplyBtn>
        </BtnDiv>
      </ModalBox>
      {completeVisible && <CompleteModal
        visible={completeVisible}
        handleModal={handleCompleteModal}
        myCoin={myCoin}
        chargeCoin={chargeCoin}
        handleCharge={handleCharge}
        handleCheck={closeModal}
        depositorName={member.name.trim()}
      />}
    </Container>
  );
};
export default CheckModal;

const Container = styled.div`
  z-index: 99;
  background-color: #00000085;
  position: fixed;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
`;

const ModalBox = styled.div`
  box-sizing: border-box;
  width: calc(100% - 24px);
  max-width: 50rem;
  margin: auto 0rem;
  background-color: var(--admin-theme-surface, white);
  color: var(--admin-ink);
  border-radius: 8px;
  padding: 34px 24px;
  div.info {
    color: var(--admin-muted);
    font-size: 14px;
    text-align: center;
    line-height: 19px;
  }
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  overscroll-behavior: contain;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-size: 20px;
  font-family: var(--font-family-base); font-weight: var(--font-weight-semibold);
`;

const SubTitle = styled.div`
  font-size: 18px;
  font-family: var(--font-family-base); font-weight: var(--font-weight-semibold);
  margin-bottom: 24px;
`;

const ContentDiv = styled.div`
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-family: var(--font-family-base); font-weight: var(--font-weight-regular);
  flex-direction: column;
  text-align: center;
  color: var(--admin-muted);
  border-top: 1px solid var(--admin-theme-border, #f0f0f0);
  border-bottom: 1px solid var(--admin-theme-border, #f0f0f0);
  margin: 30px 0px;
  gap: 8px;
  .medium {
    font-family: var(--font-family-base); font-weight: var(--font-weight-medium);
    color: var(--admin-theme-text, black);
    font-size: 16px;
  }
`;

const BtnDiv = styled(RowView)`
  position: relative;
  width: 100%;
`;

const Btn = styled.button`
  border: 0;
  &:disabled { opacity: 0.6; cursor: default; }
  border-radius: 8px;
  padding: 14px 30px;
  flex: 1;
  align-items: center;
  justify-content: center;
  display: flex;
  font-size: 16px;
  font-family: var(--font-family-base); font-weight: var(--font-weight-semibold);
  cursor: pointer;
`;

const CancelBtn = styled(Btn)`
  margin-right: 5px;
  background-color: var(--admin-theme-raised, #f8f8f8);
  color: var(--admin-muted);
`;

const ApplyBtn = styled(Btn)`
  margin-left: 5px;
  background: linear-gradient(135deg, var(--admin-accent), var(--admin-accent-deep));
  color: white;
`;

const InfoDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 130px;
  color: var(--admin-muted);
  .bankInfo {
    width: 300px;
    font-family: var(--font-family-base); font-weight: var(--font-weight-medium);
    font-size: 16px;
    color: var(--admin-theme-text, black);
    flex-direction: row;
    display: flex;

    .gray {
      color: var(--admin-muted);
    }
  }
`;

const DepositorField = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin-bottom: var(--space-6);
  label { font-size: var(--text-body-lg); font-weight: var(--font-weight-medium); }
  input {
    box-sizing: border-box;
    width: 100%;
    height: var(--control-lg);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-md);
    padding: 0 var(--space-4);
    background: var(--color-bg-surface);
    color: var(--color-text-primary);
    font: inherit;
    font-size: var(--text-body);
    &:hover { border-color: var(--color-border-strong); }
    &:focus { outline: none; border-color: var(--color-brand); box-shadow: var(--shadow-focus); }
    &::placeholder { color: var(--color-text-disabled); }
    &:disabled { background: var(--color-bg-muted); color: var(--color-text-disabled); }
  }
`;
